/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';
import type { ChangeEvent } from 'react';

import _ from 'lodash';
import { compose } from 'recompose';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import type { ActionContext } from 'js/lib/ActionContext';

import { Grid, SelectOption, SilentSelectField, Typography2 } from '@coursera/cds-core';
import { ToolbarTable } from '@coursera/coursera-ui';

import useStringSet from 'bundles/admin-shared/hooks/useStringSet';
import { compareByNaturalSortOrder } from 'bundles/author-common/utils/common';
import { CDSTable } from 'bundles/staff-management/src/cds/StaffTable/CDSTable';
import { CDSTableHeader } from 'bundles/staff-management/src/cds/StaffTable/CDSTableHeader';
import UserSearchBar from 'bundles/staff-management/src/cds/StaffTable/UserSearchBar';

import _t from 'i18n!nls/staff-management';

import StaffPermissionsLink from '../../StaffPermissionsLink';
import RowActions from '../../StaffTable/RowActions';
import type { StaffTableRow } from '../../StaffTable/types';
import { createSelector } from '../../common/memoizer';
import type { StaffManagementStoreV2 as StaffManagementStoreClass } from '../../store';
import { actions, selectors } from '../../store';
import withFluxibleContextAsProps from '../../withFluxibleContextAsProps';
import AddStaffButton from '../AddStaffButton';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import CDSLikeTable from './CDSLikeTable';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { TableBulkActionsBarRenderComponent } from './CDSLikeTable/components/TableBulkActionsBar';
import type { MembershipsByUserId, RolesById, UsersById, rolesLimitedByMemberships } from './types';

type FilterOptions = {
  value: string | number;
  label: string;
};

type StatusFilter = 'active' | 'pending';

interface InputProps {
  /** Use the table rendering method developed during [LA-256](https://coursera.atlassian.net/browse/LA-256) */
  variant?: 'CUITable' | 'CDSLikeTable' | 'CDSTable';
  searchPlaceholderText: string;
}

export interface FinalProps extends InputProps {
  onAdd(): void;
  onBatchDelete(rowOrRows: Array<StaffTableRow>): void;
  onBatchEdit(rowOrRows: Array<StaffTableRow>): void;
  height?: number;
  filterOptions?: Array<FilterOptions>;
  rows?: Array<StaffTableRow>;
}

const checkRowMatch = (searchText: string | null, row: StaffTableRow) => {
  if (!searchText || (searchText && searchText.trim() === '')) {
    return true;
  }

  const search = searchText.toLowerCase().trim();

  return row.searchString && row.searchString.indexOf(search) >= 0;
};

const staffRowMatchesRoleFilter = (row: StaffTableRow, roleFilters: string[] | undefined) => {
  if (!roleFilters || roleFilters.length === 0 || roleFilters[0] === 'All') {
    return true;
  }

  return roleFilters.some((roleFilter) => row.rolesLabel?.includes(roleFilter));
};

const staffRowMatchesStatusFilter = (row: StaffTableRow, statusFilter: StatusFilter[]) => {
  const isPending = !row.fullName; // TODO: replace with the actual status check
  return (
    statusFilter.length === 0 ||
    (statusFilter.includes('active') && !isPending) ||
    (statusFilter.includes('pending') && isPending)
  );
};

export const staffTableRowKey = (row: StaffTableRow): string => `${row.id}`;

const styles = {
  table: css`
    margin-top: var(--cds-spacing-300);
  `,
};

export const StaffTable = ({
  height,
  filterOptions,
  rows,
  onBatchDelete,
  onBatchEdit,
  variant = 'CUITable',
  searchPlaceholderText,
}: FinalProps) => {
  const [searchText, setSearchText] = useState<string | null>(null);

  const { addToSet: addToSelectedRows, removeFromSet: removeFromSelectedRows, set: selectedRows } = useStringSet();
  const [roleFilters, setRoleFilters] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter[]>([]);

  const rowState: StaffTableRow[] = React.useMemo(() => {
    if (!rows) {
      return [];
    }

    let availableRows = rows;
    if (roleFilters.length > 0 || statusFilter.length > 0) {
      availableRows = rows.filter((row) => {
        return staffRowMatchesRoleFilter(row, roleFilters) && staffRowMatchesStatusFilter(row, statusFilter);
      });
    }

    return availableRows.filter((row) => {
      return checkRowMatch(searchText, row);
    });
  }, [rows, searchText, roleFilters, statusFilter]);

  const defaultHeight = height || 1000;

  const e2eName = 'staff-management-staff-table';

  const onSelect = (row: StaffTableRow) => {
    const key = staffTableRowKey(row);
    addToSelectedRows(key);
  };

  const onDeselect = (row: StaffTableRow) => {
    const key = staffTableRowKey(row);
    removeFromSelectedRows(key);
  };

  const pickSelectedRows = () => {
    return rows?.filter((row) => {
      const key = staffTableRowKey(row);
      return selectedRows.has(key);
    });
  };

  const handleUserSearchChange = (st: string | null) => setSearchText(st);

  const handleFilterByRoleChange = (event: ChangeEvent<{ role?: string | undefined; value: unknown }>) =>
    setRoleFilters([(event.target.value as string) || '']);

  const handleBatchEdit = () => {
    const rowOrRows = pickSelectedRows();

    if (rowOrRows) {
      onBatchEdit(rowOrRows);
    }
  };

  const handleBatchDelete = () => {
    const rowOrRows = pickSelectedRows();

    if (rowOrRows) {
      onBatchDelete(rowOrRows);
    }
  };

  const columns = [
    {
      header: () => (
        <Typography2 component="p" color="supportText" display="inline">
          {_t('Name')}
        </Typography2>
      ),
      accessor: 'fullName',
      width: 280,
      cellRenderer: (name: string, row: StaffTableRow) => (
        <Typography2 component="p" className="pii-hide fs-exclude" data-e2e="row-name">
          <StaffPermissionsLink id={row.id}>{name}</StaffPermissionsLink>
        </Typography2>
      ),
      sortable: true,
      comparator: (a: string, b: string) => compareByNaturalSortOrder(a.toLowerCase(), b.toLowerCase()),
    },
    {
      header: () => (
        <Typography2 component="p" color="supportText" display="inline">
          {_t('Email')}
        </Typography2>
      ),
      accessor: 'email',
      width: 280,
      cellRenderer: (email: string) => (
        <Typography2 component="p" className="pii-hide fs-exclude" data-e2e="row-email">
          {email}
        </Typography2>
      ),
      sortable: true,
      comparator: (a: string, b: string) => compareByNaturalSortOrder(a.toLowerCase(), b.toLowerCase()),
    },
    {
      header: () => (
        <Typography2 component="p" color="supportText" display="inline">
          {_t('Roles')}
        </Typography2>
      ),
      accessor: 'rolesLabel',
      sortable: true,
      cellRenderer: (rolesLabel: string) => {
        return (
          <Typography2 component="p" data-e2e="row-roles">
            {rolesLabel}
          </Typography2>
        );
      },
    },
    {
      // Action context menu.
      accessor: 'id',
      width: 32,
      cellRenderer: (id: string, row: StaffTableRow) => (
        <div className="staff-management-row-action-buttons">
          <RowActions row={row} />
        </div>
      ),
    },
  ];

  const rowRootClassName = () => {
    return 'staff-row';
  };

  let selectOptions = [<SelectOption value="All">{_t('All')}</SelectOption>];
  if (filterOptions) {
    selectOptions = filterOptions.map(({ value, label }) => <SelectOption value={value}>{label}</SelectOption>);
    selectOptions.unshift(<SelectOption value="All">{_t('All')}</SelectOption>);
  }

  const renderRows = rowState.map((row: StaffTableRow) => ({
    ...row,
    selected: selectedRows.has(staffTableRowKey(row)),
  }));

  return (
    <div data-e2e={e2eName}>
      {variant === 'CDSTable' ? (
        <CDSTableHeader
          onSearchChange={handleUserSearchChange}
          placeholder={searchPlaceholderText}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          roleFilterOptions={filterOptions}
          roleFilters={roleFilters}
          onRoleFiltersChange={setRoleFilters}
          data-testid="cds-table-header"
        />
      ) : (
        <Grid container justifyContent="space-between" xs={12} spacing={32} alignItems="center">
          <Grid item xs={7}>
            <UserSearchBar
              // change to CDS search bar component once it is available
              onSearchChange={handleUserSearchChange}
              placeholder={searchPlaceholderText}
            />
          </Grid>
          <Grid item xs>
            <SilentSelectField
              label={_t('Filter by Role')}
              labelPlacement="none"
              placeholder={_t('Filter by Role')}
              onChange={handleFilterByRoleChange}
            >
              {selectOptions}
            </SilentSelectField>
          </Grid>
          <Grid item>
            <AddStaffButton />
          </Grid>
        </Grid>
      )}
      {variant === 'CDSTable' && (
        <CDSTable
          rows={rowState}
          totalRows={rows?.length}
          onBatchDelete={onBatchDelete}
          onBatchEdit={onBatchEdit}
          css={styles.table}
          data-testid="cds-table"
        />
      )}
      {variant === 'CDSLikeTable' && (
        <CDSLikeTable rows={rowState} onBatchDelete={onBatchDelete} onBatchEdit={onBatchEdit} />
      )}
      {variant === 'CUITable' && (
        <React.Fragment>
          <TableBulkActionsBarRenderComponent
            nothingIsSelected={selectedRows.size === 0}
            staffCount={rowState.length}
            onBatchDelete={handleBatchDelete}
            onBatchEdit={handleBatchEdit}
          />

          <Grid xs={10}>
            <ToolbarTable
              // The height is more to prevent performance problems on larger tables as it seems to
              // limit rerending to just what is visible during an action such as selecting a row.
              height={defaultHeight}
              toolbar={[]}
              enableSelection
              enableHeaderSelectAll
              rows={renderRows}
              onSelect={onSelect}
              onDeselect={onDeselect}
              columns={columns}
              rowRootClassName={rowRootClassName}
            />
          </Grid>
        </React.Fragment>
      )}
    </div>
  );
};

// The roles pertaining to users, transformed for use with the filtering of the Table component.
const filterOptionsFromState = createSelector(
  selectors.staffMemberships.getRolesLimitedByMemberships,
  (rolesLimitedByMemberships: rolesLimitedByMemberships) => {
    let filters = _.map(rolesLimitedByMemberships, (role) => ({
      value: role.name,
      label: role.name,
    }));
    filters = _.sortBy(filters, 'label');
    // Within the table we filter against the distinct role names: there might be
    // many Instructor roles depending on the number of courses available, but there is
    // only 1 "Instructor" filter option.
    return _.sortedUniqBy(filters, (filter) => filter.label);
  }
);

const isNotUndefined = (s: string | undefined): s is string => s !== undefined;

export const searchString = (roles: string[], name: string, email: string) =>
  [name.toLowerCase(), email.toLowerCase(), roles.join(' ').toLowerCase()].join(' ');

const rowsFromState = createSelector(
  selectors.staffMemberships.getMembershipsByUserId,
  selectors.staffMemberships.getRolesById,
  selectors.staffMemberships.getUsersById,
  (membershipsByUserId: MembershipsByUserId, rolesById: RolesById, usersById: UsersById): Array<StaffTableRow> => {
    return Object.values(usersById).map((user): StaffTableRow => {
      const memberships = membershipsByUserId[user.id];
      // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      const roles = memberships.map((membership) => rolesById[membership.roleId]);
      // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      const roleNames: string[] = roles.map((role) => role.name).filter(isNotUndefined);
      // Display should show that user is an Instructor (or other role type)
      // to something, not that the user has N number of Instructor roles.
      const sortedUniqueRoleNames = [...new Set(roleNames)].sort();
      const rolesLabel = sortedUniqueRoleNames.join(', ');

      const fullName = user.fullName || '';

      return {
        ...user,
        fullName,
        // In theory, dynamic row height calculation, where rows are not uniform
        // height, should work without this field, but practice (and inspection into CUI code)
        // indicates otherwise. See: https://coursera.atlassian.net/browse/DG-1970
        key: user.id + '',
        // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        memberships,
        // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        roles,
        rolesLabel,
        searchString: searchString(sortedUniqueRoleNames, fullName, user.email),
      };
    });
  }
);

export const storeConnector = (
  { StaffManagementStoreV2 }: { StaffManagementStoreV2: StaffManagementStoreClass },
  { executeAction }: ActionContext
) => {
  const state = StaffManagementStoreV2.get();
  const { fromId, type } = state;

  return {
    onAdd: () => {
      executeAction(actions.addFlow.prompt, { show: true });
    },
    onBatchDelete: (rows: Array<StaffTableRow>) => {
      return executeAction(actions.delFlow.prompt, {
        fromId: fromId as string,
        type,
        users: rows,
      });
    },
    onBatchEdit: (rows: Array<StaffTableRow>) => {
      return executeAction(actions.editFlow.prompt, {
        fromId,
        type,
        users: rows,
      });
    },
    filterOptions: filterOptionsFromState(state),
    rows: rowsFromState(state),
  };
};

export default compose<FinalProps, InputProps>(
  withFluxibleContextAsProps(),
  connectToStores<FinalProps, FinalProps & ActionContext>(['StaffManagementStoreV2'], storeConnector)
)(StaffTable);
